import { motion, AnimatePresence } from 'motion/react';
import { Message } from '@/src/lib/types';
import { Check, X, Loader2, Wrench, Clock, Sparkles, Brain } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMemo } from 'react';
import { useAppColors } from '@/src/lib/theme';
import LogoMark from '@/src/components/LogoMark';

interface MessageBubbleProps {
  message: Message;
  isLast?: boolean;
  onToolDecision?: (messageId: string, confirmed: boolean) => void;
  onSuggestionClick?: (prompt: string) => void;
}

const TOOL_LABELS: Record<string, string> = {
  list_my_shifts: 'shifts',
  list_my_tasks: 'tasks',
  get_open_tasks: 'tasks',
  get_low_stock: 'low stock',
  get_stock: 'stock',
  get_roster: 'roster',
  get_suppliers: 'suppliers',
  list_users: 'staff',
  list_pending_actions: 'approvals',
  get_deputy_roster: 'deputy roster',
  get_deputy_employees: 'deputy staff',
  create_task: 'create task',
  assign_task: 'assign task',
  reassign_task: 'reassign task',
  complete_task: 'complete task',
  reassign_deputy_shift: 'reassign shift',
  update_deputy_shift_time: 'change shift time',
};

function prettyTool(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, ' ');
}

export default function MessageBubble({ message, onToolDecision, onSuggestionClick }: MessageBubbleProps) {
  const c = useAppColors();
  const isUser = message.role === 'user';
  const showToolActions = message.isToolCall && message.toolStatus === 'request';
  const isExecuting = message.toolStatus === 'executing';
  const toolEvents = message.toolEvents ?? [];
  // De-dup chips so a tool called twice in one turn shows once.
  const uniqueEvents = useMemo(() => {
    const seen = new Map<string, { name: string; ok: boolean; pending: boolean }>();
    for (const ev of toolEvents) {
      const prior = seen.get(ev.name);
      // pending or failed beats a stale ok
      if (!prior || ev.pending || !ev.ok) seen.set(ev.name, ev);
      else if (!prior) seen.set(ev.name, ev);
    }
    return Array.from(seen.values());
  }, [toolEvents]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={`flex w-full mb-5 gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: c.iconBg }}
        >
          <LogoMark size={30} color={c.text} />
        </div>
      )}
      <div className={`flex flex-col max-w-[88%] ${isUser ? 'items-end' : 'items-start'}`}>
      <div
        className={`px-4 py-3 leading-relaxed text-[15px] relative w-fit ${
          isUser
            ? 'bg-warm-gray text-cream rounded-[20px] rounded-tr-none'
            : 'text-cream card-soft border border-cream-line rounded-2xl'
        }`}
      >
        {isExecuting && (
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
          />
        )}

        {isUser ? (
          <p className="whitespace-pre-wrap" data-no-translate>{message.content}</p>
        ) : (
          <div className="markdown-body space-y-2" data-no-translate>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <p className="leading-relaxed">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
                li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                strong: ({ children }) => <strong className="text-honey-400 font-semibold">{children}</strong>,
                em: ({ children }) => <em className="italic text-cream">{children}</em>,
                code: ({ children }) => (
                  <code className="px-1.5 py-0.5 rounded bg-leaf-400/10 text-[13px] font-mono text-leaf-text">
                    {children}
                  </code>
                ),
                h1: ({ children }) => <h1 className="text-base font-semibold text-cream mt-2 mb-1">{children}</h1>,
                h2: ({ children }) => <h2 className="text-sm font-semibold text-cream mt-2 mb-1">{children}</h2>,
                h3: ({ children }) => <h3 className="text-sm font-semibold text-cream mt-2 mb-1">{children}</h3>,
                table: ({ children }) => <div className="overflow-x-auto my-2"><table className="w-full text-xs border-collapse">{children}</table></div>,
                thead: ({ children }) => <thead>{children}</thead>,
                th: ({ children }) => <th className="text-left px-2 py-1.5 font-semibold text-honey-400 border-b border-white/10">{children}</th>,
                td: ({ children }) => <td className="px-2 py-1.5 border-b border-white/5 text-cream">{children}</td>,
                tr: ({ children }) => <tr className="hover:bg-white/5 transition-colors">{children}</tr>,
                // In-app links (#pdfedit/…, #doc/…) also work when written as a full address (http://…/#doc/…).
                a: ({ children, href: rawHref }) => { const href = rawHref?.includes('#pdfedit/') || rawHref?.includes('#doc/') ? rawHref.slice(rawHref.indexOf('#')) : rawHref; return href?.startsWith('#pdfedit/') ? (
                  // A PDF change prepared by the AI: open Quick edit with it ready to check.
                  <button type="button" onClick={() => {
                    try {
                      const raw = href.slice(9).replace(/-/g, '+').replace(/_/g, '/');
                      const json = decodeURIComponent(escape(atob(raw + '==='.slice((raw.length + 3) % 4))));
                      const d = JSON.parse(json) as { b: string; n: string; c: Array<{ f: string; r: string; near?: string }> };
                      window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'documents', pdfEdit: { bucket: d.b, name: d.n, changes: d.c.map((x) => ({ find: x.f, replace: x.r, near: x.near })) } } }));
                    } catch { /* bad link */ }
                  }} className="underline font-semibold text-lagoon-400 hover:text-lagoon-500">
                    {children}
                  </button>
                ) : href?.startsWith('#doc/') ? (
                  // A doc OpsAlo wrote: open it in Documents.
                  <button type="button" onClick={() => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'documents', docId: href.slice(5) } }))}
                    className="underline font-semibold text-lagoon-400 hover:text-lagoon-500">
                    {children}
                  </button>
                ) : (
                  <a href={href} target="_blank" rel="noreferrer" className="underline text-lagoon-400 hover:text-lagoon-500">
                    {children}
                  </a>
                ); },
              }}
            >
              {message.content || ''}
            </ReactMarkdown>
          </div>
        )}

        {isExecuting && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-3 flex items-center gap-2 text-[10px] font-black px-3 py-2 rounded-xl w-fit shadow-sm tracking-widest uppercase"
            style={{ color: c.text, background: c.iconBg, border: `1px solid ${c.border}` }}
          >
            <Loader2 size={12} className="animate-spin" />
            Secure Operative Bridge Active
          </motion.div>
        )}
      </div>

      {false && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mt-2 ml-1 flex flex-wrap gap-1.5"
        >
          {uniqueEvents.filter(ev => !['get_document','get_documents','get_roster','get_deputy_roster','get_stock','get_suppliers','get_insights','get_price_comparison','get_square_summary','get_cogs_report','list_users','get_deputy_employees','generate_daily_report'].some(t => ev.name.startsWith(t))).map((ev, i) => {
            const tone = ev.pending
              ? 'bg-honey-400/10 text-honey-400 border-honey-400/20'
              : ev.ok
              ? 'bg-leaf-400/10 text-leaf-text border-leaf-400/20'
              : 'bg-clay-400/10 text-clay-400 border-clay-400/20';
            const Icon = ev.pending ? Clock : Wrench;
            return (
              <span
                key={`${ev.name}-${i}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-md border ${tone}`}
                title={ev.pending ? 'Queued for manager approval' : ev.ok ? 'Tool succeeded' : 'Tool failed'}
              >
                <Icon size={10} />
                {prettyTool(ev.name)}
                {ev.pending && <span className="font-bold">· pending</span>}
              </span>
            );
          })}
        </motion.div>
      )}

      {!isUser && (() => {
        const done = (n: string) => uniqueEvents.some((ev) => ev.name === n && ev.ok && !ev.pending);
        const personal = done('save_memory') || done('forget_memory');
        const venue = done('save_venue_memory') || done('forget_venue_memory');
        if (!personal && !venue) return null;
        return (
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('opsalo:open-memory', { detail: { tab: venue && !personal ? 'venue' : 'personal' } }))}
            className="mt-1.5 ml-1 inline-flex items-center gap-1.5 text-[12px] font-medium hover:underline"
            style={{ color: c.textDim }}
            title="See what OpsAlo remembers"
          >
            <Brain size={13} />
            {venue && !personal ? 'Venue memory updated' : 'Memory updated'}
          </button>
        );
      })()}

      {!isUser && (message.suggestions?.length ?? 0) > 0 && onSuggestionClick && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-2 ml-1 flex flex-wrap gap-1.5"
        >
          {message.suggestions!.map((s, i) => (
            <motion.button
              key={`${s.label}-${i}`}
              whileTap={{ scale: 0.96 }}
              onClick={() => onSuggestionClick(s.prompt)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full border border-leaf-400/30 bg-leaf-500/10 text-leaf-text hover:bg-leaf-500/20 transition-colors"
            >
              <Sparkles size={11} />
              {s.label}
            </motion.button>
          ))}
        </motion.div>
      )}

      <AnimatePresence>
        {showToolActions && onToolDecision && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            className="mt-2 flex gap-2 overflow-hidden"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onToolDecision(message.id, false)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-colors"
              style={{ background: c.iconBg, color: c.textDim, borderColor: c.border }}
            >
              <X size={14} />
              Decline
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onToolDecision(message.id, true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-xl text-xs font-bold uppercase tracking-wider border border-white shadow-sm transition-all"
            >
              <Check size={14} strokeWidth={3} />
              Confirm Action
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </motion.div>
  );
}